import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { references } from '../data/mock';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Eye,
  Target,
  Heart,
  Award,
  Leaf,
  Recycle,
  Sun,
  Users,
  Shield,
  GraduationCap,
  Building,
} from 'lucide-react';

const KurumsalSection = () => {
  const { t } = useLanguage();

  const valueIcons = {
    0: <Heart className="w-5 h-5" />,
    1: <Eye className="w-5 h-5" />,
    2: <Award className="w-5 h-5" />,
    3: <Shield className="w-5 h-5" />,
    4: <Users className="w-5 h-5" />,
  };

  return (
    <div className="bg-gray-50">
      {/* Hakkımızda Section */}
      <section id="hakkimizda" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t('kurumsal.hakkimizda')}
            </h2>
            <div className="w-24 h-1 bg-amber-500 mx-auto"></div>
          </div>

          {/* Kar Yemek Hakkında */}
          <div className="mb-16">
            <Card className="border-none shadow-xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-gray-800 to-gray-700 text-white">
                <CardTitle className="text-2xl flex items-center gap-3">
                  <Building className="w-7 h-7 text-amber-400" />
                  {t('kurumsal.karYemekHakkinda')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                <p className="text-gray-700 leading-relaxed text-lg">
                  {t('about.karYemekContent')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Vizyon, Misyon, Değerler */}
          <div className="grid md:grid-cols-3 gap-8">
            {/* Vizyon */}
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow group">
              <CardHeader className="pb-4">
                <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-4 group-hover:bg-amber-500 transition-colors">
                  <Eye className="w-7 h-7 text-amber-600 group-hover:text-white transition-colors" />
                </div>
                <CardTitle className="text-xl text-gray-800">
                  {t('kurumsal.vizyon')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 leading-relaxed">
                  {t('about.vizyonContent')}
                </p>
              </CardContent>
            </Card>

            {/* Misyon */}
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow group">
              <CardHeader className="pb-4">
                <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-4 group-hover:bg-amber-500 transition-colors">
                  <Target className="w-7 h-7 text-amber-600 group-hover:text-white transition-colors" />
                </div>
                <CardTitle className="text-xl text-gray-800">
                  {t('kurumsal.misyon')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 leading-relaxed">
                  {t('about.misyonContent')}
                </p>
              </CardContent>
            </Card>

            {/* Değerlerimiz */}
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="pb-4">
                <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                  <Heart className="w-7 h-7 text-amber-600" />
                </div>
                <CardTitle className="text-xl text-gray-800">
                  {t('kurumsal.degerlerimiz')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {t('about.degerler').map((deger, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="px-4 py-2 bg-amber-50 text-amber-700 hover:bg-amber-100 flex items-center gap-2"
                    >
                      {valueIcons[index]}
                      {deger}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Sertifikalar Section */}
      <section id="sertifikalar" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t('kurumsal.sertifikalar')}
            </h2>
            <div className="w-24 h-1 bg-amber-500 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {['iso9001', 'iso22000', 'tseHyb'].map((cert, index) => (
              <Card
                key={cert}
                className="border-2 border-gray-100 hover:border-amber-400 transition-colors group"
              >
                <CardContent className="p-8 text-center">
                  <div className="w-32 h-32 mx-auto mb-6 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden group-hover:bg-amber-50 transition-colors">
                    <img
                      src={`/sertifika-${index + 1}.png`}
                      alt={t(`certificates.${cert}`)}
                      className="w-full h-full object-contain p-2"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `<div class="flex flex-col items-center"><svg class="w-16 h-16 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg><span class="text-sm text-gray-500 mt-2">Görsel Ekle</span></div>`;
                      }}
                    />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">
                    {t(`certificates.${cert}`)}
                  </h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sürdürülebilirlik Section */}
      <section
        id="surdurulebilirlik"
        className="py-20 bg-gradient-to-br from-emerald-50 to-teal-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t('kurumsal.surdurulebilirlik')}
            </h2>
            <div className="w-24 h-1 bg-emerald-500 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-all hover:-translate-y-1">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Leaf className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  {t('sustainability.cevreDostu')}
                </h3>
                <p className="text-gray-600">{t('sustainability.cevreContent')}</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-all hover:-translate-y-1">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Recycle className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  {t('sustainability.atikYonetimi')}
                </h3>
                <p className="text-gray-600">{t('sustainability.atikContent')}</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-white hover:shadow-xl transition-all hover:-translate-y-1">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Sun className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  {t('sustainability.yenilenebilirEnerji')}
                </h3>
                <p className="text-gray-600">{t('sustainability.enerjiContent')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Sosyal Sorumluluk Section */}
      <section id="sosyalsorumluluk" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t('kurumsal.sosyalSorumluluk')}
            </h2>
            <div className="w-24 h-1 bg-amber-500 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-all group">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center group-hover:bg-amber-500 transition-colors">
                  <Users className="w-8 h-8 text-amber-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  {t('socialResponsibility.toplumsal')}
                </h3>
                <p className="text-gray-600">{t('socialResponsibility.toplumContent')}</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all group">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center group-hover:bg-amber-500 transition-colors">
                  <Shield className="w-8 h-8 text-amber-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  {t('socialResponsibility.calisanHaklari')}
                </h3>
                <p className="text-gray-600">{t('socialResponsibility.calisanContent')}</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all group">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center group-hover:bg-amber-500 transition-colors">
                  <GraduationCap className="w-8 h-8 text-amber-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  {t('socialResponsibility.egitim')}
                </h3>
                <p className="text-gray-600">{t('socialResponsibility.egitimContent')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Referanslar Section */}
      <section id="referanslar" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t('kurumsal.referanslarimiz')}
            </h2>
            <div className="w-24 h-1 bg-amber-500 mx-auto"></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {references.map((ref) => (
              <Card
                key={ref.id}
                className="border-2 border-dashed border-gray-300 hover:border-amber-400 transition-colors"
              >
                <CardContent className="p-6 text-center h-32 flex items-center justify-center">
                  {ref.logo ? (
                    <img
                      src={ref.logo}
                      alt={ref.name}
                      className="max-h-full max-w-full mx-auto object-contain scale-125"
                    />
                  ) : (
                    <div className="text-gray-400">
                      <Building className="w-8 h-8 mx-auto mb-2" />
                      <p className="text-sm">{ref.name}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default KurumsalSection;